/**
 * TikTok video embed using the iframe pattern.
 *
 * Why iframe instead of blockquote + embed.js?
 *   - More reliable across regions / referrers
 *   - Faster initial paint (no JS script roundtrip)
 *   - Stable HTTPS endpoint at `https://www.tiktok.com/embed/v2/{video_id}`
 *
 * Props:
 *   - videoId: numeric TikTok video id (REQUIRED)
 *   - url: canonical TikTok URL (used as fallback link)
 *   - title: accessible iframe title
 *   - className: optional wrapper className
 */
export default function TikTokEmbed({ videoId, url, title = 'TikTok video', className = '' }) {
  if (!videoId) {
    return (
      <div className={'rounded-xl border border-white/10 bg-white/[0.02] aspect-[9/16] flex items-center justify-center text-xs text-muted-foreground ' + className}>
        Missing video id
      </div>
    );
  }
  const src = `https://www.tiktok.com/embed/v2/${videoId}?lang=en-US`;
  return (
    <div
      className={'relative w-full overflow-hidden rounded-xl border border-white/10 bg-black ' + className}
      style={{ aspectRatio: '9 / 16' }}
      data-testid="tiktok-embed"
    >
      <iframe
        src={src}
        title={title}
        allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowFullScreen
        scrolling="no"
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 h-full w-full border-0"
        style={{ background: '#000' }}
      />
      {/* Fallback link in case iframe is blocked entirely */}
      <noscript>
        <a
          href={url || `https://www.tiktok.com/video/${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="absolute inset-0 flex items-center justify-center text-white"
        >
          Open on TikTok
        </a>
      </noscript>
    </div>
  );
}
